import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { Ago, EmptyBlock, SectionCard } from "../../components/primitives";
import { formatClock, formatMs } from "../../format";
import type { BranchMeta, PipelineSection } from "../../types";
import { Chip, InlineStat, PizzaRef, VerdictBadge } from "./shared";

const COLLAPSED = 12;
const GRID =
  "md:grid md:grid-cols-[6.5rem_minmax(0,2fr)_minmax(0,1.2fr)_7rem_minmax(4.5rem,6rem)_minmax(4.5rem,6rem)] md:items-start md:gap-x-3";

/** The newest captures of the window, one line per pizza. */
export function RecentCard({ data, branchOf }: { data: PipelineSection; branchOf: (id: string | null | undefined) => BranchMeta | null }) {
  const { t, tp } = useLocalization();
  const [all, setAll] = useState(false);
  const recent = [...data.recent].sort((a, b) => b.capture_at - a.capture_at);
  const shown = all ? recent : recent.slice(0, COLLAPSED);
  const name = (id: string | null) => branchOf(id)?.name || id || t("monitoring.unknownBranch");

  return (
    <SectionCard
      title={t("monitoring.overview.recentCaptures")}
      description={t("monitoring.overview.recentCapturesDescription")}
      actions={
        recent.length > COLLAPSED ? (
          <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setAll((v) => !v)}>
            {all ? t("monitoring.pipeline.recent.showLess") : t("monitoring.overview.showAll")}
          </Button>
        ) : undefined
      }
      contentClassName="p-0"
    >
      {recent.length === 0 ? (
        <div className="p-4">
          <EmptyBlock>{t("monitoring.overview.noCaptures")}</EmptyBlock>
        </div>
      ) : (
        <div className="text-xs">
          <div className={cn(GRID, "hidden border-b px-4 py-2 text-[11px] font-medium text-muted-foreground")}>
            <span>{t("monitoring.col.time")}</span>
            <span>{t("monitoring.col.pizza")}</span>
            <span>{t("monitoring.col.branch")}</span>
            <span>{t("monitoring.col.verdict")}</span>
            <span className="text-right">{t("monitoring.kpi.verdict")}</span>
            <span className="text-right">{t("monitoring.pipeline.recent.uploadDelay")}</span>
          </div>
          <ul className="divide-y divide-border/60">
            {shown.map((pizza) => (
              <li
                key={`${pizza.id}-${pizza.capture_at}`}
                className={cn(GRID, "flex flex-col gap-1 px-4 py-2", pizza.verdict === "missing" && "bg-red-500/[0.04]")}
              >
                <div className="flex items-baseline justify-between gap-2 md:block">
                  <span className="tabular-nums">{formatClock(pizza.capture_at)}</span>
                  <Ago ts={pizza.capture_at} className="text-[11px] text-muted-foreground md:block" />
                </div>
                <div className="flex min-w-0 items-start justify-between gap-2">
                  <PizzaRef id={pizza.id} name={pizza.name} />
                  <div className="md:hidden">
                    <VerdictBadge verdict={pizza.verdict} />
                  </div>
                </div>
                <div className="min-w-0 break-words text-muted-foreground md:text-foreground">
                  {name(pizza.branch_id)}
                  {pizza.capture_count > 1 && (
                    <Chip className="ml-1.5 align-middle">{tp("monitoring.pipeline.captureCount", pizza.capture_count)}</Chip>
                  )}
                </div>
                <div className="hidden min-w-0 flex-col items-start gap-0.5 md:flex">
                  <VerdictBadge verdict={pizza.verdict} />
                  {pizza.classification && (
                    <span className="break-words text-[10px] text-muted-foreground">{pizza.classification}</span>
                  )}
                </div>
                <div className="flex flex-wrap gap-x-3 md:contents">
                  <InlineStat label={<span className="md:hidden">{t("monitoring.kpi.verdict")}</span>} className="md:justify-end md:font-mono">
                    {formatMs(pizza.capture_to_verdict_ms)}
                  </InlineStat>
                  <InlineStat
                    label={<span className="md:hidden">{t("monitoring.pipeline.recent.uploadDelay")}</span>}
                    className="md:justify-end md:font-mono"
                  >
                    {pizza.upload_received ? formatMs(pizza.upload_delay_ms) : <span className="text-muted-foreground">{t("monitoring.pipeline.recent.noPhoto")}</span>}
                  </InlineStat>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </SectionCard>
  );
}
